import { useState } from 'react';

import type { PaymentMethod } from './processingFee';

export interface MethodCompletion {
  /** All three card Elements report themselves complete. */
  isCardComplete: boolean;
  isBankAccountComplete: boolean;
  onCardNumberChange: (isComplete: boolean) => void;
  onCardExpiryChange: (isComplete: boolean) => void;
  onCardCvcChange: (isComplete: boolean) => void;
  onBankAccountChange: (isComplete: boolean) => void;
}

/** Nothing filled in. The starting point for each method, and what a method
 *  reverts to the moment it stops being the selected one. */
const NOTHING_COMPLETE = {
  number: false,
  expiry: false,
  cvc: false,
  bankAccount: false,
};

/**
 * Whether the Stripe Elements for the chosen method are filled in.
 *
 * An Element is an iframe, so its contents cannot be read — only its own
 * `onChange` knows whether it is complete. The legacy portal instead attached a
 * `MutationObserver` to each element's class list and watched for
 * `StripeElement--complete`, rebuilding the observers on every toggle; that is
 * a workaround for an API it already had, and it leaked observers whenever the
 * cleanup raced the re-render.
 *
 * The method that the flags belong to is stored alongside them, so switching
 * method discards them during render rather than in an effect afterwards.
 * Selecting a different method unmounts its Elements, and a stale `true` would
 * enable the pay button over fields no longer on screen — the reason this
 * cannot simply be left to accumulate.
 */
export const useMethodCompletion = (method: PaymentMethod): MethodCompletion => {
  const [state, setState] = useState({ method, ...NOTHING_COMPLETE });

  const current = state.method === method ? state : { method, ...NOTHING_COMPLETE };

  // Functional update, not a spread of the rendered `current`: autofill makes
  // all three card Elements report completion inside one React batch, and
  // spreading a value captured at render time would let each write clobber the
  // one before it, leaving the pay button disabled over a filled-in form.
  const set = (field: keyof typeof NOTHING_COMPLETE) => (isComplete: boolean) => {
    setState((previous) => {
      const base = previous.method === method ? previous : { method, ...NOTHING_COMPLETE };
      return { ...base, [field]: isComplete };
    });
  };

  return {
    isCardComplete: current.number && current.expiry && current.cvc,
    isBankAccountComplete: current.bankAccount,
    onCardNumberChange: set('number'),
    onCardExpiryChange: set('expiry'),
    onCardCvcChange: set('cvc'),
    onBankAccountChange: set('bankAccount'),
  };
};
